import { useState, useRef, useEffect, Fragment } from "react";
import VisibilityApi from "../state/Visibility-api";
import { motion, AnimatePresence } from "framer-motion";
import Utils from "../util/Utils";
import { textAlign } from "@mui/system";
import { configStore } from "../state/CommonState";
import { getCutOffForPorequalsPol, getCutOffForPorNotEqualsPol } from "../util/constants";

export default function UpdateCutOffPopup({ isOpen, onClose, anchorRef }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);
    const [revisedValues, setRevisedValues] = useState({});

    const popupRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);

        const fetchData = async () => {
            try {
                const response = await VisibilityApi.getCutOffSegregation();
                const cleaned = response.filter(item => {
                    const { por, pol, fl_vessel_number, fl_vessel_name, mlo, etd_at_pol } = item._id;
                    return [por, pol, fl_vessel_number, fl_vessel_name, mlo, etd_at_pol].every(val => val && val.trim && val.trim() !== "");
                });
                setData(cleaned);
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [isOpen]);

    const handleInputChange = (key, field, value, item) => {
        setRevisedValues((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                [field]: value
            },
            _id: item._id,
            isUs: item._id.isPodUS
        }));
    };


    const handleSubmit = async () => {
        try {
            setLoading(true);
            const res = await Promise.resolve(VisibilityApi.updateCutOffs(revisedValues));

            console.log(res, 'Response from updateCutOffs');

            setLoading(false);
            onClose();
            const updatedBookings = res.updatedIds?.length
                ? `\nUpdated Bookings:\n${res.updatedIds.join(", ")}`
                : "";

            configStore.setModalMessage(`${res.message}${updatedBookings}`);
        } catch (err) {
            console.error('Error in handleSubmit:', err);
            setError(err.message || 'An error occurred');
            setLoading(false);
        }
    };

    const getPopupPosition = () => {
        if (!anchorRef.current) return {};

        const buttonRect = anchorRef.current.getBoundingClientRect();
        const popupWidth = 900;
        const popupPadding = 16;
        const spacing = 8;

        let left = buttonRect.left + window.scrollX;
        let top = buttonRect.bottom + window.scrollY + spacing;

        // Ensure popup stays within viewport
        if (left + popupWidth > window.innerWidth) {
            left = window.innerWidth - popupWidth - popupPadding;
        }
        if (left < popupPadding) {
            left = popupPadding;
        }

        return {
            position: "absolute",
            top: `${top}px`,
            left: `${left}px`,
            width: `${popupWidth}px`,
            minWidth: "900px",
            zIndex: 1000,
        };
    };


    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        zIndex: 999,
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "center",
                        overflowY: "auto",
                        paddingTop: "80px"
                    }}
                    onClick={onClose}
                >
                    <motion.div
                        ref={popupRef}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: "white",
                            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                            borderRadius: "10px",
                            width: "900px",
                            maxHeight: "90vh",
                            overflow: "hidden",
                            padding: "16px",
                            position: "relative"
                        }}
                        initial={{ opacity: 0, scale: 0.9, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: -10 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                    >
                        <header
                            style={{
                                padding: "12px 40px 12px 12px",
                                backgroundColor: process.env.REACT_APP_ENTERPRISE_COLOUR,
                                color: "white",
                                fontWeight: "bold",
                                textAlign: "center",
                                position: "relative",
                            }}
                        >
                            Update Cut-Offs in Group
                            <button
                                onClick={onClose}
                                style={{
                                    position: "absolute",
                                    top: "8px",
                                    right: "12px",
                                    background: "transparent",
                                    border: "none",
                                    fontSize: "20px",
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                    color: "white",
                                    lineHeight: 1,
                                }}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </header>

                        <section style={{ overflowY: "auto", maxHeight: "450px", padding: "10px" }}>

                            {loading ? (
                                <p>Loading...</p>
                            ) : error ? (
                                <p style={{ color: "red" }}>Error: {error}</p>
                            ) : data.length > 0 ? (
                                <div style={{ width: "100%", overflowY: "auto", maxHeight: "350px" }}>
                                    <table
                                        style={{
                                            width: "100%",
                                            tableLayout: "fixed",
                                            borderCollapse: "collapse",
                                        }}
                                    >
                                        <thead>
                                            <tr style={{ backgroundColor: "#e9ecef" }}>
                                                <th style={{ padding: "8px" }}>POR</th>
                                                <th style={{ padding: "8px" }}>POL</th>
                                                <th style={{ padding: "8px" }}>Voyage Number</th>
                                                <th style={{ padding: "8px" }}>Vessel Name</th>
                                                <th style={{ padding: "8px" }}>Carrier Name</th>
                                                <th style={{ padding: "8px" }}>ETD</th>
                                                <th style={{ padding: "8px" }}>Bookings Count</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.map((item, index) => (
                                                <Fragment key={index}>
                                                    <tr
                                                        onClick={() => {
                                                            setExpandedRow(expandedRow === index ? null : index)
                                                            setRevisedValues({});
                                                        }}
                                                        style={{ cursor: "pointer", backgroundColor: expandedRow === index ? "#f1f8ff" : "white" }}
                                                    >
                                                        <td>{item._id.por || "-"}</td>
                                                        <td>{item._id.pol || "-"}</td>
                                                        <td>{item._id.fl_vessel_number || "-"}</td>
                                                        <td>{item._id.fl_vessel_name || "-"}</td>
                                                        <td>{item._id.mlo || "-"}</td>
                                                        <td>{item._id.etd_at_pol || "-"}</td>
                                                        <td>{item.count || "-"}</td>
                                                    </tr>

                                                    {expandedRow === index && (
                                                        <tr>
                                                            <td colSpan="7" style={{ paddingBottom: "15px", borderBottom: "2px solid #ccc" }}>
                                                                <table style={{ width: "100%", marginTop: "10px", borderCollapse: "collapse", textAlign: 'center' }}>
                                                                    <thead>
                                                                        <tr style={{ backgroundColor: "#dbeafe" }}>
                                                                            <th>Cut-Off Type</th>
                                                                            <th>Original</th>
                                                                            <th>Revise To</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {Object.entries(item._id.pol === item._id.por ? getCutOffForPorequalsPol(item._id.isPodUS) : getCutOffForPorNotEqualsPol(item._id.isPodUS)).map(([key, cutOff], cutOffIndex) => (
                                                                            <tr key={cutOffIndex}>
                                                                                <td style={{ textAlign: "left" }}>{cutOff}</td>
                                                                                <td>{Utils.checkIsISoString(item?.[key]?.[0]) ? Utils.formatToDateTime(item?.[key]?.[0]) : (item?.[key]?.[0] || "-")}</td>
                                                                                <td>
                                                                                    <input

                                                                                        type="datetime-local"
                                                                                        style={{
                                                                                            width: "100%",
                                                                                            border: "2px solid #ccc",
                                                                                            borderRadius: "4px",
                                                                                        }}
                                                                                        value={revisedValues?.dateTime?.[key]}
                                                                                        onChange={(e) => handleInputChange(key, "dateTime", e.target.value, item)}
                                                                                    />


                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>

                                                                </table>
                                                                {Object.keys(revisedValues).length >= 1 && (
                                                                    <button
                                                                        onClick={() => handleSubmit(index)}
                                                                        style={{
                                                                            marginTop: "10px",
                                                                            padding: "8px 12px",
                                                                            backgroundColor: process.env.REACT_APP_ENTERPRISE_COLOUR,
                                                                            color: "white",
                                                                            border: "none",
                                                                            cursor: "pointer",
                                                                            borderRadius: "5px",
                                                                            float: "right",
                                                                        }}
                                                                    >
                                                                        Submit
                                                                    </button>)}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <p>No cut-off details available.</p>
                            )}
                        </section>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
